// eslint-disable-next-line import/no-unresolved
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Keyboard, A11y } from 'swiper/modules';
import { useIntl } from 'react-intl';

import React from 'react';
import { bpCompatibleAccessoriesCarousel } from '../../Carousel/breakpoints';
import TextLink from '../../UI/TextLink/TextLink';
import ProductCard from '../../Carousel/ProductCard/ProductCard';

import {
  CompatibleAccessoriesWrapper,
  CopyContainer,
  Carousel,
} from './styles';

interface Props {
  data: Record<string, any>;
  locale?: string;
  heading: Record<string, any>;
  phone?: string;
}

const CompatibleAccessories = ({
  data,
  locale,
  heading,
  phone,
}: Props) => {
  const intl = useIntl();
  if (!data.length) return null;

  // Create product card slide
  const slide = (item) => (
    <SwiperSlide key={item.id} className="accessory">
      <ProductCard
        data={item}
        className="product-carousel"
        type="accessory"
      />
    </SwiperSlide>
  );

  // SORT AND FILTER PRODUCTS TO BE PLACED IN THE CAROUSEL
  // Sort Cases
  const itemCases = data
    .filter((cases) => cases?.type.key === '40')
    .map((item) => slide(item));

  // Sort Screen Protectors
  const itemScreenProtectors = data
    .filter((screens) => screens?.type.key === '42')
    .map((item) => slide(item));

  // Sort Wall Chargers
  const itemWallChargers = data
    .filter((wallChargers) => wallChargers?.type.key === '18')
    .map((item) => slide(item));

  // Sort Charging Cables
  const itemChargingCables = data
    .filter((chargingCables) => chargingCables?.type.key === '345')
    .map((item) => slide(item));

  // Sort Headphones
  const itemHeadphones = data
    .filter(
      (headphones) => headphones?.type.key === '30'
        || headphones?.type.key === '31'
        || headphones?.type.key === '36'
        || headphones?.type.key === '29',
    )
    .map((item) => slide(item));

  // Create new array of top selling product cards
  const topAccessories = [];

  // Push product cards by group into new array
  topAccessories.push(
    ...itemCases,
    ...itemScreenProtectors,
    ...itemWallChargers,
    ...itemChargingCables,
    ...itemHeadphones,
  );

  // Create writable access to new array of top selling products
  let currentAccessories = topAccessories;

  // Make a list of all the top items for each slot of the carousel
  const removeAccessories = [
    itemCases[0], // ---------------- slot1
    itemCases[1], // ---------------- slot2
    itemCases[2], // ---------------- slot3
    itemScreenProtectors[0], // ----- slot4
    itemWallChargers[0], // --------- slot5
    itemChargingCables[0], // ------- slot6
    itemHeadphones[0], // ----------- slot7
  ];

  // Remove the top (top selling) products from the new array and leave a backup list of the remaining top items
  currentAccessories = currentAccessories?.filter(
    (item) => !removeAccessories.includes(item),
  );

  // Function to replace accessory if it doesn't exist with first item of the top (top selling) products and then remove it from the list array
  const setSelectedTopAccessory = () => {
    const selectedTopAccessory = currentAccessories[0];

    currentAccessories = currentAccessories.filter(
      (item) => item !== selectedTopAccessory,
    );

    return selectedTopAccessory;
  };

  // Set each slot to the top product (if one exists) or replace with top remaining item from another category
  const slot1 = itemCases.length >= 1 ? itemCases[0] : setSelectedTopAccessory();
  const slot2 = itemCases.length >= 2 ? itemCases[1] : setSelectedTopAccessory();
  const slot3 = itemCases.length >= 3 ? itemCases[2] : setSelectedTopAccessory();
  const slot4 = itemScreenProtectors.length >= 1
    ? itemScreenProtectors[0]
    : setSelectedTopAccessory();
  const slot5 = itemWallChargers.length >= 1
    ? itemWallChargers[0]
    : setSelectedTopAccessory();
  const slot6 = itemChargingCables.length >= 1
    ? itemChargingCables[0]
    : setSelectedTopAccessory();
  const slot7 = itemHeadphones.length >= 1 ? itemHeadphones[0] : setSelectedTopAccessory();

  return (
    <CompatibleAccessoriesWrapper>
      <CopyContainer>
        {heading && <p>{heading}</p>}
        <TextLink
          route="internal"
          linkText={intl.formatMessage({
            id: 'SeeAll',
          })}
          locale={locale}
          url={`/accessories/?phone=${phone}`}
        />
      </CopyContainer>
      <Carousel>
        <Swiper
          tabIndex="0"
          speed={1000}
          breakpoints={bpCompatibleAccessoriesCarousel}
          navigation
          pagination={{
            clickable: true,
          }}
          a11y={{
            slideRole: 'group',
            enabled: true,
          }}
          allowTouchMove
          keyboard
          modules={[Navigation, Pagination, Keyboard, A11y]}
          className="swiper"
        >
          {/* If there are 7 or less total accessories for a product, show them all */}
          {data.length <= 7 ? (
            data.map((item) => slide(item))
          ) : (
            <>
              {slot1}
              {slot2}
              {slot3}
              {slot4}
              {slot5}
              {slot6}
              {slot7}
            </>
          )}
        </Swiper>
      </Carousel>
    </CompatibleAccessoriesWrapper>
  );
};

export default CompatibleAccessories;
